/**
 * Dex OCR region-of-interest geometry + exclusion (Push 5 / v1.4.3 b5).
 *
 * Why this module exists
 * ----------------------
 * The live Pokédex scanner read garbage dex numbers (a Kanto screen showing
 * #0093–#0112 produced #0001/#0002/#0004/#0009…). Two geometry bugs caused
 * it, both inside the per-frame OCR in pokedexOcr.extractPokedexFrame:
 *
 *   1. WRONG COLUMN COUNT. The grid sweep used 3 columns, but the Pokémon GO
 *      Pokédex is a 4×4 grid. With 3 anchors over a 4-wide grid the crops
 *      straddled cell boundaries, so the number band of one cell overlapped
 *      the sprite of its neighbour and OCR returned noise.
 *
 *   2. WRONG NUMBER ROI. The dex number was cropped from the TOP-LEFT of each
 *      cell. In Pokémon GO the dex number is rendered in the BOTTOM-CENTRE of
 *      the cell, under the sprite. Cropping the top-left read sprite pixels /
 *      sparkle badges, which OCR mangled into small stray digits (0001…).
 *
 * On top of that the scanner had NO exclusion for:
 *   - the iOS status bar / Dynamic Island (clock "8:31" → "0831"/"831"),
 *   - the in-app Picture-in-Picture overlay ("Reading 4 × 4 grid", a dex
 *     count, etc.) which floats over the bottom-right of the capture,
 *   - the Pokémon GO header count ("150") and the bottom SHINY/LUCKY/XXL
 *     selector labels.
 *
 * This module is PURE and DOM-free. It owns:
 *   - the canonical 4×4 cell geometry,
 *   - the bottom-centre number ROI for a cell,
 *   - rectangle-overlap based exclusion (status bar, PiP, header, selector),
 *   - a single `acceptCellNumberRoi` validator the OCR loop and the tests
 *     both call, so "would this screen accept #0093 and reject the header
 *     150 / the selector labels?" is provable in a unit test.
 *
 * All coordinates are fractions of the full frame (0–1), x→right, y→down.
 */

/** A rectangle in fractional frame coordinates. */
import { PHONE_LAYOUT, type DexScreenLayout } from "./dexLayout";

export interface FracRect {
  x: number;
  y: number;
  w: number;
  h: number;
}

/**
 * Canonical exclusion bands of a Pokémon GO Pokédex capture, as fractions
 * of the frame. Calibrated against the 19.5:9 reference screenshots
 * (IMG_7632 etc.). These are the areas OCR text must NOT be sourced from.
 */
export interface DexExclusionZones {
  /** iOS status bar / Dynamic Island band at the very top. */
  statusBar: FracRect;
  /** Pokémon GO region header + count card ("KANTO  150  Recently registered"). */
  header: FracRect;
  /** Bottom SHINY / LUCKY / XXL selector + search/close FAB band. */
  selector: FracRect;
  /**
   * In-app Picture-in-Picture overlay. PoGo's PiP floats over the lower
   * region of the capture (see "Reading 4 × 4 grid" bubble in IMG_7635).
   * Null when PiP is known to be closed for this frame.
   */
  pip: FracRect | null;
}

/**
 * Default exclusion zones. The PiP rectangle matches the observed floating
 * window position (bottom-right ~lower third). Callers that know PiP is
 * closed should pass `pip: null` so they don't over-mask.
 */
export const DEFAULT_EXCLUSION_ZONES: DexExclusionZones = {
  statusBar: { x: 0, y: 0, w: 1, h: 0.055 },
  // b13c.4: header card runs to ~0.205 on device (KANTO/count + "Recently
  // registered" row), and the first grid row top is ~0.225 — keep the band just
  // shy of the grid so it masks the count but never a row-0 number.
  header: { x: 0, y: 0.055, w: 1, h: 0.15 },
  selector: { x: 0, y: 0.86, w: 1, h: 0.14 },
  // b17d Fix 1: the in-app PiP pill ("Reading 4 × 4 grid") now snaps TOP-RIGHT,
  // not bottom-right. The TestFlight capture (IMG_9346) showed the old
  // bottom-right pill (y 0.66→0.90) sitting on dex grid rows 4-5 (cells 13-18),
  // masking/misreading the very numbers the scan needs. The 4-row grid runs from
  // ~0.205 down to GRID_BOTTOM (0.79; corrected b18), so the top-right strip ABOVE the first row
  // (between the status bar at 0.055 and the first grid row at ~0.19) is the
  // only corner that clears every cell. The exclusion band tracks the pill there
  // so it overlaps the header zone, never a grid row. See pipScreenMode
  // modeToPlacement / pipOverlap recommendedSnapCorner — both also moved to
  // top-right for the pokedex target so the placement policy and the OCR mask
  // agree on where the pill lives.
  pip: { x: 0.5, y: 0.055, w: 0.5, h: 0.135 },
};

/** Build exclusion zones, toggling the PiP rectangle on/off.
 *
 * PR4: when a per-frame `gridBottom` anchor is supplied (detected device
 * geometry), the bottom `selector` band is re-based to start just below the
 * detected grid bottom instead of the fixed 0.86 fraction. This keeps the
 * SHINY/LUCKY/XXL selector + carousel masked on devices where that band sits
 * lower or higher than the 19.5:9 default, without clipping a legitimately
 * low-sitting bottom grid row. The status bar / header / PiP rects are device-
 * anchored at the TOP (status bar + Dynamic Island are fixed-height regardless
 * of aspect ratio) so they are left unchanged. Omitting `gridBottom` (or
 * passing the default) reproduces the legacy zones exactly.
 */
export function exclusionZones(
  pipActive: boolean,
  gridBottom: number = GRID_BOTTOM,
  pipRect: FracRect | null = null,
  /** Screen layout (lib/dexLayout); the phone layout reproduces the legacy zones exactly. */
  layout: DexScreenLayout = PHONE_LAYOUT,
): DexExclusionZones {
  const base: DexExclusionZones = layout.id === "phone" ? DEFAULT_EXCLUSION_ZONES : layout.zones;
  const usingDefault = Math.abs(gridBottom - layout.gridBottom) < 1e-9;
  // The selector band historically starts at 0.86 with the grid bottom at
  // 0.66 — a 0.20 gap that covers the FAB row + the filter-pill row + the
  // carousel below it. Preserve that gap relative to the detected bottom, and
  // always run the band to the frame edge (h such that y + h = 1).
  const selectorY = usingDefault
    ? base.selector.y
    : Math.min(0.95, gridBottom + 0.20);
  const selector = usingDefault
    ? base.selector
    : { x: 0, y: selectorY, w: 1, h: Math.max(0.02, 1 - selectorY) };
  // #1b: when a live PiP rect is supplied (detected per-frame), the PiP mask
  // follows the real overlay wherever the user parked it (bottom-left,
  // bottom-right, or top) instead of the fixed top-right DEFAULT rect. A small
  // pad widens the detected card so anti-aliased text at its edge is still
  // covered. Falls back to the fixed rect when no rect was detected so the
  // upload path and the no-detection case cannot regress.
  let pip: FracRect | null = null;
  if (pipActive) {
    if (pipRect) {
      const pad = 0.01;
      const px = Math.max(0, pipRect.x - pad);
      const py = Math.max(0, pipRect.y - pad);
      pip = {
        x: px,
        y: py,
        w: Math.min(1 - px, pipRect.w + 2 * pad),
        h: Math.min(1 - py, pipRect.h + 2 * pad),
      };
    } else {
      pip = base.pip;
    }
  }
  return {
    ...base,
    selector,
    pip,
  };
}

/** True when two fractional rectangles overlap by any non-zero area. */
export function rectsOverlap(a: FracRect, b: FracRect): boolean {
  return (
    a.x < b.x + b.w &&
    a.x + a.w > b.x &&
    a.y < b.y + b.h &&
    a.y + a.h > b.y
  );
}

/**
 * Fraction of rect `a` that lies inside rect `b` (0–1). Used so a number ROI
 * that merely clips the very edge of an exclusion band isn't discarded, but
 * one substantially covered by PiP/header/etc. is.
 */
export function overlapFraction(a: FracRect, b: FracRect): number {
  const ix = Math.max(0, Math.min(a.x + a.w, b.x + b.w) - Math.max(a.x, b.x));
  const iy = Math.max(0, Math.min(a.y + a.h, b.y + b.h) - Math.max(a.y, b.y));
  const inter = ix * iy;
  const area = a.w * a.h;
  if (area <= 0) return 0;
  return inter / area;
}

/**
 * The grid geometry of the Pokémon GO Pokédex, in fractional coords. Kept here
 * (not imported from dexImagePipeline) so this module stays a standalone,
 * dependency-light validator surface for the OCR loop.
 *
 * Most variants render 4 columns. G-Max is the lone exception: 3 columns wide
 * because G-Max sprites are noticeably wider. Pass `variant` into the geometry
 * helpers (or call `gridColumnsForVariant`) when sweeping a non-default variant.
 */
export const GRID_COLUMNS = 4;
/** Number of grid columns for G-Max layouts. */
export const GMAX_GRID_COLUMNS = 3;
/** Maximum number of grid rows derived per frame. */
export const GRID_ROWS = 4;

/**
 * Resolve the grid column count for a variant. Defaults to `GRID_COLUMNS` (4).
 * The variant is intentionally typed as a string so this module stays free of
 * `PokedexVariant` imports — callers pass `frameVariant` straight through.
 */
export function gridColumnsForVariant(
  variant?: string | null,
): number {
  return variant === "gmax" ? GMAX_GRID_COLUMNS : GRID_COLUMNS;
}
export const GRID_LEFT = 0.02;
export const GRID_RIGHT = 0.98;

// b13c.5: BOTTOM-ANCHORED vertical ROI derivation.
//
// Pokémon GO's Pokédex header animates dynamically — it is large at the top of
// the list, shrinks as you scroll down, expands again on scroll-up and lingers
// ~2s before collapsing. There is no static `GRID_TOP` that survives all those
// states, which is why b13c.4 (and earlier) drifted: a fixed top anchor either
// clipped row 0 or read header text. What IS stable across every header state
// is the white filter-pills row at the bottom (LUCKY / XXL / XXS / search / X /
// REGIONS): it sits at the same screen position regardless of header size.
//
// So we anchor the BOTTOM of the grid to that pills row and walk UPWARD by the
// row pitch to derive each row's top/bottom. A per-frame safety check drops any
// derived row whose top would fall inside the maximum header extent (only ever
// trips when the user has scrolled the grid up under an expanded header).
//
// Calibration (verified against 3 device captures, 923×2000, incl. top-of-dex
// with expanded header — proportional bounds transfer to the 886×1918 frame):
/**
 * Bottom of the 4-row grid page (fractional). CORRECTED 2026-06-18 (b18):
 * the prior value 0.66 was the TOP of the floating filter-pills/REGIONS row —
 * NOT the grid bottom. The dex grid scrolls CONTINUOUSLY BEHIND those floating
 * buttons, so row 4 (e.g. #0013-#0016) is real, scannable data sitting at
 * ~0.755. Anchoring the grid to the pills row truncated row 4 and compressed
 * all 4 rows upward, mis-slicing every cell (classifyDexCell returned
 * pos-absent 16/16). Overlay-validated on the 923x2000 reference (IMG_0901):
 * 4 rows span ~0.205 -> 0.79, all 16 number ROIs centred on their digits.
 */
export const GRID_BOTTOM = 0.79;
/** Per-row pitch == (GRID_BOTTOM - HEADER_MAX_BOTTOM) / 4 = (0.79-0.205)/4. Corrected b18. */
export const ROW_HEIGHT = 0.14625;
/**
 * Tightest expanded-header bottom. Row 0001 starts at ~0.190 even with the
 * expanded header, so 0.185 preserves it while still gating any row that truly
 * overlaps the header. All 4 derived rows have top > 0.185, so this is
 * defensive — it does not fire in the normal (scrolled) case.
 *
 * ROW-0 TOP-OF-LIST OCCLUSION (G-Max, 2026-06-23). The G-Max top-row blank
 * (device telemetry: row 0 crop at y≈0.205 returned foregroundPixels 0) is NOT
 * a geometry bug — `dexGridRows()` already yields a row-0 whose `numberRoiFrac`
 * (y≈0.255–0.345) sits BELOW this header floor and is accepted by
 * `acceptCellNumberRoi`. The blank is a DEVICE-RENDER occlusion: at the very top
 * of the Pokédex list PoGo paints an EXPANDED header card over the first grid
 * row, so the crop captures header background, not the sprite/number. That band
 * (0.055–0.205) also holds the live count card + the "Recently registered"
 * mini-sprites, whose numbers are real G-Max member ids — so scanning ABOVE this
 * floor to "recover" row 0 would false-write header content. The safe and
 * already-working recovery is the user's own scroll: pulling the row down past
 * the header (mid-screen) makes it read like any other row. We therefore do NOT
 * move the floor up; row-0 geometry is correct for the scrolled case and an
 * occluded row-0 cell must remain a rejected/empty read, never a write. See the
 * row-0 geometry tests in dexGmaxThreeColumn.test.ts.
 */
export const HEADER_MAX_BOTTOM = 0.205;

/** A derived grid row in fractional frame coordinates. `row` is 0 = topmost. */
export interface DexRow {
  top: number;
  bottom: number;
  row: number;
}

/**
 * Derive the visible grid rows for a frame by anchoring the bottom of the grid
 * to the filter-pills row and walking up by `ROW_HEIGHT`. Returns up to
 * `GRID_ROWS` rows, dropping any whose top overlaps the maximum (expanded)
 * header extent — so length is 4 in the normal case and can shrink to 3 only
 * when the grid is scrolled up under an expanded header. All bounds are
 * fractions of frame height (0–1).
 *
 * PR4 (device-relative ROIs): `gridBottom` defaults to the fixed `GRID_BOTTOM`
 * constant (preserving every existing callsite and test bit-for-bit), but a
 * caller that has detected the real grid bottom per-frame
 * (dexAnchorDetector.detectGridBottomFrac) passes that fraction in so the rows
 * track the actual device geometry instead of a 19.5:9 constant. The row pitch
 * scales with the anchor so a taller/shorter grid still tiles 4 rows: when an
 * explicit anchor is given we derive `ROW_HEIGHT` proportionally from it rather
 * than using the fixed pitch, keeping the bottom row pinned to the detected
 * bottom and row 0 starting just below the header.
 *
 * 3-COLUMN PAGES (G-Max, 2026-08-31 fixture audit IMG_1205). Cells keep their
 * aspect ratio, so a page that is 3 cells wide is 4/3 of a row TALLER per row:
 * the real pitch measured on the fixture is ~0.1925 vs the 4-column 0.14625,
 * and tiling FOUR short rows put every number ROI in a gutter — the whole top
 * row and every centred (never-seen) number were simply never read, which is
 * why a correctly-routed G-Max frame still committed nothing. Passing the
 * frame's column count scales the pitch by (4 / columns); the header floor
 * then naturally trims the walk to 3 rows. Verified against the fixture: the
 * 3-row lattice reproduces the hand-labelled sidecar 9/9 where the 4-row one
 * mis-sliced every cell.
 */
export function dexGridRows(
  gridBottom: number = GRID_BOTTOM,
  columns: number = GRID_COLUMNS,
  /**
   * GRID PHASE (2026-09-08): vertical lattice correction in CELL HEIGHTS,
   * positive = the real grid sits BELOW the assumed lattice (the
   * dexGridPhase estimate). Every row shifts by the same amount, the pitch
   * is untouched, a row pushed under the header is dropped and a row the
   * shift reveals above the pills is added, and the kept rows are renumbered
   * from 0 so the row-0 protections keep applying to the topmost one.
   * 0 = the unshifted lattice, byte-identical to before.
   */
  phaseOffsetCells: number = 0,
  /**
   * Screen layout (lib/dexLayout, 2026-10-23): the phone layout IS the
   * constants above, so the default reproduces every legacy row exactly; the
   * tablet layout walks a 4:3 iPad's wider pitch under its taller header.
   */
  layout: DexScreenLayout = PHONE_LAYOUT,
): DexRow[] {
  // Back-compat: with the default fixed anchor we use the fixed pitch exactly,
  // so the legacy proportional bounds (and every existing test) are unchanged.
  // With a detected anchor we scale the pitch so the same rows span from the
  // header floor down to the detected bottom — this is what makes the grid
  // device-relative. We keep the bottom row pinned to `gridBottom`.
  const usingDefault = Math.abs(gridBottom - layout.gridBottom) < 1e-9;
  // Wider cells (fewer columns) = proportionally taller rows, same aspect.
  const colScale = GRID_COLUMNS / Math.max(1, columns);
  const visibleRows = Math.max(1, Math.round(GRID_ROWS / colScale));
  const rowHeight = usingDefault
    ? layout.rowHeight * colScale
    : (gridBottom - layout.headerMaxBottom) / visibleRows;
  const rows: DexRow[] = [];
  const shift = Number.isFinite(phaseOffsetCells) ? phaseOffsetCells * rowHeight : 0;
  // Loop over the COLUMN-SCALED row count (4 for 4-col — behaviour-identical
  // to the old GRID_ROWS loop — 3 for G-Max), so the visible rows stay
  // numbered from 0 and the row-0 protections (top-row skip, header guard)
  // keep applying to the TOPMOST on-screen row on every layout. One extra
  // candidate row below the lattice is considered so an UPWARD phase shift
  // can reveal the row it uncovers above the pills; with no shift its top
  // sits exactly on the grid bottom and it is never kept.
  for (let r = 0; r <= visibleRows; r++) {
    // r = 0 is topmost, r = visibleRows - 1 is the bottommost (pinned to anchor).
    const bottom = gridBottom - (visibleRows - 1 - r) * rowHeight + shift;
    const top = bottom - rowHeight;
    // The header floor is a hard cut for the unshifted lattice (byte-identical
    // to before). A phase-shifted lattice keeps a row that a small shift nudged
    // a little under the floor: dropping the whole top row for an 0.07-cell
    // correction lost four readable cells (2026-09-08 harness). Half the row
    // must still clear the floor.
    const floorCut = shift === 0 ? layout.headerMaxBottom - 1e-9 : layout.headerMaxBottom - rowHeight * 0.5;
    if (top < floorCut) continue; // would overlap expanded header
    if (top >= gridBottom - rowHeight * 0.25) continue; // nothing of it above the pills
    rows.push({ top, bottom, row: rows.length });
  }
  return rows;
}

/**
 * Rectangle of cell (row, col) in fractional coords. With b13c.5 the vertical
 * extent comes from a bottom-anchored `DexRow` (see `dexGridRows`); the
 * horizontal layout tiles `columns` evenly across GRID_LEFT→GRID_RIGHT. Pass a
 * row from `dexGridRows()` and the column count from `gridColumnsForVariant`
 * (defaults to 4 for back-compat).
 */
export function cellRectFrac(
  rowSpec: DexRow,
  col: number,
  columns: number = GRID_COLUMNS,
  layout: DexScreenLayout = PHONE_LAYOUT,
): FracRect {
  const gridW = layout.gridRight - layout.gridLeft;
  const colW = gridW / columns;
  return {
    x: layout.gridLeft + col * colW,
    y: rowSpec.top,
    w: colW,
    h: rowSpec.bottom - rowSpec.top,
  };
}

/**
 * The dex-number ROI for a cell. Pokémon GO renders the "#0093" label in TWO
 * different vertical positions depending on whether the entry is caught:
 *   - UNCAUGHT cell (no sprite): the number is VERTICALLY CENTRED in the cell.
 *   - CAUGHT cell (sprite shown): the number is pinned to the BOTTOM-CENTRE,
 *     under the sprite.
 *
 * b13c.4 fix: the prior ROI only covered the lower ~26% band, so it read the
 * number for caught cells but MISSED it on uncaught cells — the dominant cause
 * of "3–5 cells instead of 16" (only caught entries, on rows that happened to
 * align, ever produced a read). We now span the lower ~62% of the cell
 * (vertical centre through bottom) so a single ROI catches the number in either
 * position. Still centred horizontally (66% of cell width) so it excludes the
 * "New" badge in the top-right and the sprite edges left/right.
 */
export function numberRoiFrac(cell: FracRect): FracRect {
  const w = cell.w * 0.66;
  const h = cell.h * 0.62;
  return {
    x: cell.x + (cell.w - w) / 2,
    // Start just below the cell's vertical midpoint-minus-margin so the band
    // covers the centred (uncaught) number and extends down to the bottom-
    // pinned (caught) number.
    y: cell.y + cell.h * 0.34,
    w,
    h,
  };
}

/** Why a candidate cell's number ROI was rejected (diagnostics vocabulary). */
export type RoiRejectReason =
  | "ok"
  | "in_status_bar"
  | "in_header"
  | "in_selector"
  | "pip_obscured"
  | "outside_grid";

export interface RoiDecision {
  accept: boolean;
  reason: RoiRejectReason;
}

/** A number ROI must be at least this clear of an exclusion band to count. */
const MAX_TOLERATED_OVERLAP = 0.2;

/**
 * Decide whether the number ROI of a grid cell is a trustworthy place to
 * read a dex number from. Rejects ROIs that fall in / are substantially
 * covered by the status bar, header count, bottom selector, or PiP overlay,
 * and ROIs that have drifted outside the horizontal grid extent.
 *
 * This is the single validator the OCR loop and the fixture tests share.
 */
export function acceptCellNumberRoi(
  roi: FracRect,
  zones: DexExclusionZones = DEFAULT_EXCLUSION_ZONES,
  layout: DexScreenLayout = PHONE_LAYOUT,
): RoiDecision {
  if (roi.x < layout.gridLeft - 1e-6 || roi.x + roi.w > layout.gridRight + 1e-6) {
    return { accept: false, reason: "outside_grid" };
  }
  if (overlapFraction(roi, zones.statusBar) > MAX_TOLERATED_OVERLAP) {
    return { accept: false, reason: "in_status_bar" };
  }
  if (overlapFraction(roi, zones.header) > MAX_TOLERATED_OVERLAP) {
    return { accept: false, reason: "in_header" };
  }
  if (overlapFraction(roi, zones.selector) > MAX_TOLERATED_OVERLAP) {
    return { accept: false, reason: "in_selector" };
  }
  if (zones.pip && overlapFraction(roi, zones.pip) > MAX_TOLERATED_OVERLAP) {
    return { accept: false, reason: "pip_obscured" };
  }
  return { accept: true, reason: "ok" };
}

/**
 * Enumerate the accepted number ROIs for a frame, given which exclusion
 * zones are live. Also returns how many candidate cells were dropped because
 * PiP obscured them — surfaced separately in diagnostics, per the brief.
 */
export interface AcceptedRois {
  rois: Array<{ row: number; col: number; roi: FracRect }>;
  pipObscured: number;
  rejected: Record<Exclude<RoiRejectReason, "ok">, number>;
}

export function acceptedNumberRois(
  zones: DexExclusionZones = DEFAULT_EXCLUSION_ZONES,
  gridRows: DexRow[] = dexGridRows(),
  columns: number = GRID_COLUMNS,
  layout: DexScreenLayout = PHONE_LAYOUT,
): AcceptedRois {
  const rois: AcceptedRois["rois"] = [];
  const rejected: AcceptedRois["rejected"] = {
    in_status_bar: 0,
    in_header: 0,
    in_selector: 0,
    pip_obscured: 0,
    outside_grid: 0,
  };
  for (const rowSpec of gridRows) {
    for (let col = 0; col < columns; col++) {
      const roi = numberRoiFrac(cellRectFrac(rowSpec, col, columns, layout));
      const d = acceptCellNumberRoi(roi, zones, layout);
      if (d.accept || d.reason === "ok") {
        rois.push({ row: rowSpec.row, col, roi });
      } else {
        rejected[d.reason] += 1;
      }
    }
  }
  return { rois, pipObscured: rejected.pip_obscured, rejected };
}
